import CancelIcon from "@mui/icons-material/Clear";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLatestPermanentBid,
  fetchPermanentBidPDF,
  deletePermanentBidEvent,
  fetchLatestPermanentBidEventsByEidAndDate,
} from "../api/bid";
import { fetchEmployee } from "../api/employee";
import { isUserAuthorizedByServer } from "../api/userAccess";
import { getPresignedUrl, uploadFileToS3 } from "../api/system";
import HolidayPreference from "../components/HolidayPreference";
import PageLoading from "../components/PageLoading";
import PermanentBidPreference from "../components/PermanentBidPreference";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import ViewCommentsDialog from "../components/ViewCommentsDialog";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import {
  copyJsonObject,
  getAdminStation,
  getMultiStationAdminStationList,
  sortOnCaseSensitiveTexts,
} from "../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  CANCEL,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  CLIENT,
  EMPLOYEE_ID,
  HOME_PATH,
  IAM_CONTRACT,
  INDEX_TO_CHOICE_MAP,
  MULTI_STATION_ADMIN_ACCESS,
  NON_CONTRACT,
  PERMANENT_BID_CHAR_LIMIT,
  STATION,
  SUBMIT,
  DELETE,
  TWU_CONTRACT,
  API_TIMEOUT,
  API_POLLING_INTERVAL,
  FEATURES,
} from "../util/constants";
import { PERM_BID_UPDATE_TYPE } from "../util/bid/bidConstants.js";
import { getUserDataFromEmployeeService } from "../util/employeeDataRetriever";
import { validateEmployee } from "../util/inputValidation";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  CANCEL_TITLE,
  DELETE_PERMANENT_BID_ADMIN_TITLE,
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  CUSTOMER_SERVICE_PERMANENT_BID_MESSAGE,
  ERROR_MESSAGE,
  EXPORT_PDF,
  INVALID_EMPLOYEE_ID,
  LAST_UPDATED,
  MANAGE_PERMANENT_BID_TITLE,
  PERMANENT_BID_CANCEL_MESSAGE,
  PERMANENT_BID_DELETE_MESSAGE_ADMIN,
  PERMANENT_BID_NON_CONTRACT_ERROR_MESSAGE,
  PERMANENT_BID_SAVE_AS_ADMIN_MESSAGE,
  PERMANENT_BID_SUBMISSION_ERROR_MESSAGE,
  PERMANENT_BID_DELETE_AS_ADMIN_MESSAGE,
  PERMANENT_BID_DELETION_ERROR_MESSAGE,
  STATION_FILTER_MUST_BE_SELECTED,
  SUBMIT_PERMANENT_BID_ADMIN_TITLE,
  SUBMIT_PERMANENT_BID_AS_ADMIN_DIALOG_TITLE,
  SUCCESS_TITLE,
  TWU_PERMANENT_BID_MESSAGE,
  UPDATE_PERMANENT_BID_FIELDS_ALERT_MESSAGE,
  UPDATE_PERMANENT_BID_TWU_ALERT_MESSAGE,
} from "../util/messageConstants";
import {
  areDuplicatePreferencesExists,
  choiceErrorsInitialState,
  choicesInitialState,
  formatHolidayChoices,
  validateChoices,
  validateEmployeeDataForAdminPermanentBid,
  validatePermanentBidEmployeeId,
  validatePreferences,
} from "../util/permanentBidInputValidation";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../util/timeZoneService";
import { getUnionContractGivenJobCode, getWorkgroupGivenJobCode } from "../util/workgroupMapper";
import isEqual from "lodash.isequal";
import { getBidName } from "../util/bid/shiftBidUtils";

const useStyles = makeStyles((theme) => ({
  lastUpdated: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  pageTitleLeft: {
    fontSize: "24px",
    maxWidth: "90vw",
    textAlign: "left",
    marginTop: "10px",
  },
  root: {
    margin: "auto",
    width: "100%",
    marginLeft: "2rem",
    paddingRight: "4rem",
  },
  buttonStyle: {
    padding: "15px",
  },
  cardStyle: {
    marginTop: "40px",
    borderRadius: 0,
  },
  inputText: {
    width: "15vw",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  inputSelect: {
    maxWidth: "17vw",
    margin: "auto",
    textAlign: "left",
    width: "90px",
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "16px",
    marginTop: "-5px",
  },
  bidDesktopScroll: {
    height: "100%",
    overflowY: "scroll",
    overflowX: "hidden",
    position: "absolute",
    textAlign: "left",
    width: "100%",
    paddingBottom: "100px",
  },
  bidMarginTop: {
    marginTop: 0,
  },
  bidButton: {
    width: "100px",
  },
  bidAlert: {
    textAlign: "left",
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: "14px",
  },
  CardContent: {
    padding: 0,
  },
  employeeDetails: {
    backgroundColor: theme.palette.secondary.swaGray1,
    height: "auto",
    borderRadius: "5px",
  },
  employeeName: {
    fontSize: "24px",
    paddingTop: "11px",
    paddingLeft: "16px",
    textAlign: "left",
  },
  employeeData: {
    textAlign: "left",
    maxHeight: "56px",
  },
  link: {
    display: "none",
  },
  bidInputRow: {
    display: "flex",
  },
  bidInput: {
    paddingLeft: "10px",
  },
}));

const AdminPermanentBid = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const dispatch = useDispatch();
  const [station, setStation] = useState("");
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [permanentBid, setPermanentBid] = useState([]);
  const [employeeID, setEmployeeId] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeIdSearchLoaded, setEmployeeIdSearchLoaded] = useState(false);
  const [employeeIdSearchTriggered, setEmployeeIdSearchTriggered] = useState(false);
  const [contract, setContract] = useState("");
  const [dayShiftPreference, setDayShiftPreference] = useState("");
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [christmasChoices, setChristmasChoices] = useState(choicesInitialState);
  const [thanksgivingChoices, setThanksgivingChoices] = useState(choicesInitialState);
  const [thanksgivingChoiceErrors, setThanksgivingChoiceErrors] = useState(choiceErrorsInitialState);
  const [christmasChoiceErrors, setChristmasChoiceErrors] = useState(choiceErrorsInitialState);
  const [dayShiftPreferenceError, setDayShiftPreferenceError] = useState(false);
  const [lastUpdatedTimestamp, setLastUpdatedTimestamp] = useState("");
  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");
  const [lastUpdatedComments, setLastUpdatedComments] = useState("");
  const [isLastUpdatedByAdmin, setIsLastUpdatedByAdmin] = useState(false);
  const [noPreferencesAlert, setNoPreferencesAlert] = useState(false);
  const [savePermanentBidLoading, setSavePermanentBidLoading] = useState(false);
  const [isDeletePermanentBidLoading, setIsDeletePermanentBidLoading] = useState(false);
  const [isErrorPermanentBidDelete, setIsErrorPermanentBidDelete] = useState(false);
  const [isDeleteSuccessDialogOpen, setIsDeleteSuccessDialogOpen] = useState(false);
  const [permanentBidVersion, setPermanentBidVersion] = useState(null);
  const [christmasChoicesInitialState, setChristmasChoicesInitialState] = useState(choicesInitialState);
  const [thanksgivingChoicesInitialState, setThanksgivingChoicesInitialState] = useState(choicesInitialState);
  const [permanentBidInitialValues, setPermanentBidInitialValues] = useState([]);
  const [dayShiftPreferenceInitialState, setDayShiftPreferenceInitialState] = useState("");
  const [twuContract, setTwuContract] = useState(false);
  const [isErrorPermanentBidSave, setIsErrorPermanentBidSave] = useState(false);
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isPermanentBidEmpty, setIsPermanentBidEmpty] = useState(true);
  const linkRef = useRef();
  const [expanded, setExpanded] = React.useState(true);
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  useEffect(() => {
    setIsPermanentBidEmpty(true);
  }, [employeeID]);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (props.isAwardedEmpReadOnlyView) {
        setStation(props.bidData.station);
        setEmployeeId(props.selectedAwardedBid.awardedEid);
        fetchEmployeeData(props.selectedAwardedBid.awardedEid);
      } else {
        setStation(getAdminStation(props.featureConfig.userStations, userSession, stationFilter));
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const downloadPDF = async () => {
    setExportLoading(true);
    try {
      let res = await fetchPermanentBidPDF(employeeID);
      res = await res.json();

      if (!res.error && !res.message) {
        const a = linkRef.current;
        a.href = res.downloadUrl;
        a.click();
        a.href = "";
        setExportLoading(false);
      } else {
        console.error("error while downloading permanent bid pdf!");
      }
    } catch {
      console.error("error while downloading permanent bid pdf!");
    }
  };

  const clearBottomForm = () => {
    setEmployeeIdErrors("");
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(false);
    setEmployeeData(null);
    setDayShiftPreferenceInitialState("");
    setPermanentBidInitialValues([]);
    setChristmasChoicesInitialState(choicesInitialState);
    setThanksgivingChoicesInitialState(choicesInitialState);
    setDayShiftPreference("");
    setPermanentBid([]);
    setChristmasChoices(choicesInitialState);
    setThanksgivingChoices(choicesInitialState);
    setLastUpdatedTimestamp("");
    setPermanentBidVersion(null);
  };

  const handleStationFilterChange = (event) => {
    clearBottomForm();
    setEmployeeId("");
    setStation(event.target.value);
    dispatch(updateStationFilter(event.target.value));
  };

  const handleEmployeeIdChange = (eid) => {
    if (employeeData) {
      setEmployeeData(null);
    }
    setLastUpdatedTimestamp("");
    setEmployeeId(eid.target.value.toLowerCase().trim());
  };

  const handleDayShiftPreference = (event) => {
    setDayShiftPreference(event.target.value);
    setDayShiftPreferenceError(false);
  };

  const isPermanentBidChanged = () => {
    const filteredPreferences = permanentBid.map(({ id, message, ...rest }) => rest);
    const filteredPreferencesInitialValues = permanentBidInitialValues.map(({ id, message, ...rest }) => rest);
    return (
      !isEqual(filteredPreferences, filteredPreferencesInitialValues) ||
      !isEqual(christmasChoices, christmasChoicesInitialState) ||
      !isEqual(thanksgivingChoices, thanksgivingChoicesInitialState) ||
      dayShiftPreferenceInitialState !== dayShiftPreference
    );
  };

  const fetchEmployeeData = (eid) => {
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(true);
    setEmployeeIdErrors("");
    setEmployeeData(null);
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let filteredEmployeeData = await getUserDataFromEmployeeService(result);
          let employeeDataErrors = "";
          if (!props.isAwardedEmpReadOnlyView) {
            employeeDataErrors = validateEmployeeDataForAdminPermanentBid(
              filteredEmployeeData,
              props.isAwardedEmpReadOnlyView ? props.bidData.station : station
            );
          }
          if (employeeDataErrors === "") {
            employeeDataErrors = validateEmployee(
              filteredEmployeeData.eid,
              filteredEmployeeData.firstName,
              filteredEmployeeData.lastName,
              filteredEmployeeData.activeStation,
              getWorkgroupGivenJobCode(filteredEmployeeData.jobCode)
            );

            setEmployeeData(filteredEmployeeData);
            const userContract = getUnionContractGivenJobCode(filteredEmployeeData.jobCode);
            setContract(userContract);
            if (userContract === TWU_CONTRACT) {
              setTwuContract(true);
            } else {
              setTwuContract(false);
            }
            if (userContract === NON_CONTRACT) {
              setEmployeeIdErrors(PERMANENT_BID_NON_CONTRACT_ERROR_MESSAGE);
              setEmployeeIdSearchLoaded(true);
            } else {
              fetchPermanentBid();
            }
          } else {
            setEmployeeIdErrors(employeeDataErrors);
            setEmployeeIdSearchLoaded(true);
          }
        } else {
          setEmployeeIdErrors(INVALID_EMPLOYEE_ID);
          setEmployeeIdSearchLoaded(true);
        }
      });
  };

  const setPermanentBidData = (res) => {
    if (
      !res.error &&
      !res.message &&
      res.latestPermanentBidEvent &&
      res.latestPermanentBidEvent.updateType !== "DELETE"
    ) {
      const latestPermanentBid = res.latestPermanentBidEvent;
      const preferences = latestPermanentBid.preferences.map((preference, i) => ({
        id: i + 1,
        daysOff: preference.daysOff,
        position: preference.position,
        startTime: `${preference.startTimeStartRange.slice(0, 2)}:${preference.startTimeStartRange.slice(
          2
        )}-${preference.startTimeEndRange.slice(0, 2)}:${preference.startTimeEndRange.slice(2)}`,
        startTimeStartRange: preference.startTimeStartRange,
        startTimeEndRange: preference.startTimeEndRange,
      }));

      const christmasHolidayChoices = {};
      latestPermanentBid?.christmas?.forEach(
        (choiceType, i) => (christmasHolidayChoices[choiceType] = INDEX_TO_CHOICE_MAP[i])
      );
      const thanksgivingHolidayChoices = {};
      latestPermanentBid?.thanksgiving?.forEach(
        (choiceType, i) => (thanksgivingHolidayChoices[choiceType] = INDEX_TO_CHOICE_MAP[i])
      );
      setDayShiftPreferenceInitialState(latestPermanentBid?.catchAllPreference);
      setPermanentBidInitialValues(preferences);
      setChristmasChoicesInitialState(christmasHolidayChoices);
      setThanksgivingChoicesInitialState(thanksgivingHolidayChoices);

      setDayShiftPreference(latestPermanentBid?.catchAllPreference);
      setPermanentBid(preferences);
      setChristmasChoices(christmasHolidayChoices);
      setThanksgivingChoices(thanksgivingHolidayChoices);
      setLastUpdatedTimestamp(parseInt(latestPermanentBid?.createdAt));
      setLastUpdatedByFirstName(latestPermanentBid.modifiedByFirstName);
      setLastUpdatedByLastName(latestPermanentBid.modifiedByLastName);
      setLastUpdatedComments(latestPermanentBid.comments);
      setIsLastUpdatedByAdmin(latestPermanentBid.flags.isAdminCreated);
      setPermanentBidVersion(latestPermanentBid?.v);
      setIsPermanentBidEmpty(false);
    } else {
      setDayShiftPreferenceInitialState("");
      setPermanentBidInitialValues([]);
      setChristmasChoicesInitialState(choicesInitialState);
      setThanksgivingChoicesInitialState(choicesInitialState);

      setDayShiftPreference("");
      setPermanentBid([]);
      setChristmasChoices(choicesInitialState);
      setThanksgivingChoices(choicesInitialState);
      setLastUpdatedTimestamp("");
      setPermanentBidVersion(res.error || res.message ? null : res.latestPermanentBidEvent.v);

      setIsPermanentBidEmpty(true);

      if (res?.latestPermanentBidEvent?.updateType === "DELETE") {
        setLastUpdatedByFirstName(res.latestPermanentBidEvent.modifiedByFirstName);
        setLastUpdatedByLastName(res.latestPermanentBidEvent.modifiedByLastName);
        setLastUpdatedTimestamp(parseInt(res.latestPermanentBidEvent.createdAt));
        setLastUpdatedComments(res.latestPermanentBidEvent.comments);
        setIsLastUpdatedByAdmin(res.latestPermanentBidEvent.flags.isAdminCreated);
      }
    }
    setEmployeeIdSearchLoaded(true);
  };

  const fetchPermanentBid = () => {
    setEmployeeIdSearchLoaded(false);
    setChristmasChoiceErrors(choiceErrorsInitialState);
    setThanksgivingChoiceErrors(choiceErrorsInitialState);
    setDayShiftPreferenceError(false);
    setNoPreferencesAlert(false);

    if (props.isAwardedEmpReadOnlyView) {
      fetchLatestPermanentBidEventsByEidAndDate(props.selectedAwardedBid.awardedEid, props.bidData)
        .then((result) => result.json())
        .then((res) => {
          setPermanentBidData(res);
        })
        .catch((err) => {
          console.log("error fetching latest permanent bid!", err);
          setEmployeeIdSearchLoaded(true);
        });
    } else {
      fetchLatestPermanentBid(employeeID)
        .then((result) => result.json())
        .then((res) => {
          setPermanentBidData(res);
        })
        .catch((err) => {
          console.log("error fetching latest permanent bid!", err);
          setEmployeeIdSearchLoaded(true);
        });
    }
  };

  // fetch perm bid till it matches requestId
  const longPollingPermBidFetch = (requestId) => {
    const startTime = DateTime.now();

    function fetchBid() {
      fetchLatestPermanentBid(employeeID)
        .then((result) => result.json())
        .then((res) => {
          if (res?.latestPermanentBidEvent?.requestId === requestId) {
            setPermanentBidData(res);
            setIsSubmitDialogOpen(true);
            setSavePermanentBidLoading(false);
            setIsSuccessDialogOpen(false);
          } else if (!res || res.error) {
            // stop polling if one call fails
            setIsSubmitDialogOpen(true);
            setSavePermanentBidLoading(false);
            setIsSuccessDialogOpen(false);
            setIsErrorPermanentBidSave(true);
          } else {
            const currentTime = DateTime.now();
            const elapsedTime = currentTime - startTime;
            if (elapsedTime < API_TIMEOUT) {
              // recursive call till requestId matches or timeout happens
              setTimeout(fetchBid, API_POLLING_INTERVAL);
            } else {
              // stop polling when timeout
              setIsSubmitDialogOpen(true);
              setSavePermanentBidLoading(false);
              setIsSuccessDialogOpen(false);
              setIsErrorPermanentBidSave(true);
            }
          }
        })
        .catch((err) => {
          console.log("error fetching latest permanent bid!", err);
          setIsSubmitDialogOpen(true);
          setSavePermanentBidLoading(false);
          setIsSuccessDialogOpen(false);
          setIsErrorPermanentBidSave(true);
        });
    }

    fetchBid();
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event?.target?.value?.trim();

    if (trimEmpId !== employeeData?.eid) {
      clearBottomForm();
    }
    let errors = validatePermanentBidEmployeeId(trimEmpId, userSession.eid);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!employeeData || trimEmpId !== employeeData.eid) {
        fetchEmployeeData(trimEmpId);
      }
    }
  };

  const handleView = (eid) => {
    if (!employeeData || eid !== employeeData.eid) {
      let errors = validatePermanentBidEmployeeId(eid, userSession.eid);
      if (errors === "") fetchEmployeeData(eid);
      if (employeeIdErrors === "" || errors !== "") {
        setEmployeeIdErrors(errors);
      }
    }
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleNoClickedCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  const handleClickDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleClickYesDeleteDialog = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > PERMANENT_BID_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(PERMANENT_BID_CHAR_LIMIT));
    } else {
      setIsDeletePermanentBidLoading(true);
      deletePermanentBid();
    }
  };

  const handleClickCancelDialogOpen = () => {
    setIsCancelDialogOpen(true);
    setIsSuccessDialogOpen(false);
  };

  const handleClickSubmitDialogOpen = () => {
    setIsValidationLoading(true);
    if (areAllValidationsPassing()) {
      setIsSuccessDialogOpen(true);
    }
    setIsValidationLoading(false);
  };

  const handleCancelDialogYesButtonClicked = () => {
    setIsCancelDialogOpen(false);
  };

  const handleSubmitDialogYesButtonClicked = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > PERMANENT_BID_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(PERMANENT_BID_CHAR_LIMIT));
    } else {
      setSavePermanentBidLoading(true);
      savePermanentBid();
    }
  };

  const areAllValidationsPassing = () => {
    const isPreferencesValidationPassing = checkPreferences();
    if (contract === IAM_CONTRACT) {
      const isPreferenceTypeValidationPassing = validatePreferenceType();
      const isThanksgivingChoicesValidationPassing = validateThanksGivingChoices();
      const isChristmasChoicesValidationPassing = validateChristmasChoices();
      if (!isThanksgivingChoicesValidationPassing || !isChristmasChoicesValidationPassing) {
        setExpanded(true);
      }
      return (
        isPreferenceTypeValidationPassing &&
        isThanksgivingChoicesValidationPassing &&
        isChristmasChoicesValidationPassing &&
        isPreferencesValidationPassing
      );
    } else {
      return isPreferencesValidationPassing;
    }
  };

  const validatePreferenceType = () => {
    setDayShiftPreferenceError(false);
    if (dayShiftPreference === "") {
      setDayShiftPreferenceError(true);
      return false;
    }
    return true;
  };

  const validateThanksGivingChoices = () => {
    setThanksgivingChoiceErrors(copyJsonObject(choiceErrorsInitialState));
    const errors = validateChoices(thanksgivingChoices);
    setThanksgivingChoiceErrors(errors);
    return Object.values(errors).every((value) => value === "");
  };

  const validateChristmasChoices = () => {
    setChristmasChoiceErrors(copyJsonObject(choiceErrorsInitialState));
    const errors = validateChoices(christmasChoices);
    setChristmasChoiceErrors(errors);
    return Object.values(errors).every((value) => value === "");
  };

  const checkPreferences = () => {
    const validatedTableData = validatePreferences(permanentBid);
    if (!permanentBid.length) {
      setNoPreferencesAlert(true);
    } else {
      setNoPreferencesAlert(false);
      setPermanentBid(validatedTableData);
    }
    return !areDuplicatePreferencesExists(validatedTableData) && permanentBid.length;
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(lastUpdatedTimestamp, station).toFormat("MM/dd/yyyy T ZZZZ");
  };

  const savePermanentBid = () => {
    if (areAllValidationsPassing() && isPermanentBidChanged()) {
      const preferences = permanentBid.map(({ id, startTime, message, ...rest }) => rest);
      const thanksgivingChoicesForPayload = formatHolidayChoices(thanksgivingChoices);
      const christmasChoicesForPayload = formatHolidayChoices(christmasChoices);
      const payload = {
        eid: employeeID,
        contract: contract,
        createdBy: CLIENT,
        v: permanentBidVersion,
        workgroup: getWorkgroupGivenJobCode(employeeData.jobCode),
        flags: { isAdminCreated: true },
        station: station,
        preferences: preferences,
        catchAllPreference: !twuContract ? dayShiftPreference : null,
        thanksgiving: !twuContract ? thanksgivingChoicesForPayload : null,
        christmas: !twuContract ? christmasChoicesForPayload : null,
        comments: comments,
        updateType: PERM_BID_UPDATE_TYPE.CREATE,
        modifiedByEid: userSession.eid,
        modifiedByFirstName: userSession.firstName,
        modifiedByLastName: userSession.lastName,
      };
      getPresignedUrl("put_object", "data-passthrough", `PERM_BID_${employeeID}%2Fperm_bid.json`)
        .then((res) => res.json())
        .then((result) => {
          if (result.presignedUrl) {
            uploadFileToS3(result.presignedUrl, payload).then((response) => {
              console.log(response);
              if (response.status === 200) {
                console.log("Success!");
                const requestId = response.headers.get("x-amz-request-id");
                longPollingPermBidFetch(requestId);
              } else {
                console.error("Failure in bid S3 upload!");
                setIsSubmitDialogOpen(true);
                setSavePermanentBidLoading(false);
                setIsSuccessDialogOpen(false);
                setIsErrorPermanentBidSave(true);
              }
            });
          } else {
            console.error("Failure in getting S3 presigned url!");
            setIsSubmitDialogOpen(true);
            setSavePermanentBidLoading(false);
            setIsSuccessDialogOpen(false);
            setIsErrorPermanentBidSave(true);
          }
        });
    } else {
      setIsSuccessDialogOpen(false);
      setSavePermanentBidLoading(false);
    }
  };

  const deletePermanentBid = async () => {
    const postBody = {
      eid: employeeID,
      createdBy: "CLIENT",
      flags: { isAdminCreated: true },
      updateType: "DELETE",
      modifiedByFirstName: userSession.firstName,
      modifiedByLastName: userSession.lastName,
      modifiedByEid: userSession.eid,
      station: station,
      contract: contract,
      comments: comments,
      workgroup: getWorkgroupGivenJobCode(employeeData.jobCode),
      v: permanentBidVersion,
    };
    await deletePermanentBidEvent(postBody)
      .then((res) => res.json())
      .then(async (response) => {
        if (!response.message) {
          console.log(response);
          setIsDeleteDialogOpen(false);
          setIsDeletePermanentBidLoading(false);
          setIsErrorPermanentBidDelete(false);
          setIsDeleteSuccessDialogOpen(true);
        } else {
          throw new Error();
        }
      })
      .catch(async (err) => {
        console.log("error: ", err);
        setIsDeleteDialogOpen(false);
        setIsDeletePermanentBidLoading(false);
        setIsErrorPermanentBidDelete(true);
        setIsDeleteSuccessDialogOpen(true);
      });
  };

  const renderCancelAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        title={CANCEL_TITLE}
        isDialogOpen={isCancelDialogOpen}
        message={PERMANENT_BID_CANCEL_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA)}
        handleNoButtonClicked={handleNoClickedCancelDialog}
        redirectTo={HOME_PATH}
        handleYesButtonClicked={handleCancelDialogYesButtonClicked}
      />
    );
  };

  const renderDeleteAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        title={DELETE_PERMANENT_BID_ADMIN_TITLE}
        isDialogOpen={isDeleteDialogOpen}
        message={PERMANENT_BID_DELETE_MESSAGE_ADMIN}
        handleNoButtonClicked={() => {
          setIsDeleteDialogOpen(false);
          setCommentsError(false);
          setCommentErrorMessage("");
          setComments("");
        }}
        handleYesButtonClicked={handleClickYesDeleteDialog}
        isYesLoading={isDeletePermanentBidLoading}
        hasComments
        comments={comments}
        setComments={setComments}
        commentsError={commentsError}
        commentErrorMessage={commentErrorMessage}
        setCommentsError={setCommentsError}
        setCommentErrorMessage={setCommentErrorMessage}
        commentCharLimit={PERMANENT_BID_CHAR_LIMIT}
        yesTitle={DELETE}
        noTitle={CANCEL}
      />
    );
  };

  const renderSubmitAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        title={SUBMIT_PERMANENT_BID_ADMIN_TITLE}
        isDialogOpen={isSuccessDialogOpen}
        message={SUBMIT_PERMANENT_BID_AS_ADMIN_DIALOG_TITLE}
        handleNoButtonClicked={() => {
          setIsSuccessDialogOpen(false);
          setCommentsError(false);
          setCommentErrorMessage("");
          setComments("");
        }}
        handleYesButtonClicked={handleSubmitDialogYesButtonClicked}
        isYesLoading={savePermanentBidLoading}
        hasComments
        comments={comments}
        setComments={setComments}
        commentsError={commentsError}
        commentErrorMessage={commentErrorMessage}
        setCommentsError={setCommentsError}
        setCommentErrorMessage={setCommentErrorMessage}
        commentCharLimit={PERMANENT_BID_CHAR_LIMIT}
        yesTitle={SUBMIT}
        noTitle={CANCEL}
      />
    );
  };

  const renderHolidayComponent = () => (
    <HolidayPreference
      dayShiftPreference={dayShiftPreference}
      setDayShiftPreference={setDayShiftPreference}
      handleDayShiftPreference={handleDayShiftPreference}
      dayShiftPreferenceError={dayShiftPreferenceError}
      isDesktopView={props.isDesktopView}
      christmasChoices={christmasChoices}
      setChristmasChoices={setChristmasChoices}
      thanksgivingChoices={thanksgivingChoices}
      setThanksgivingChoices={setThanksgivingChoices}
      thanksgivingChoiceErrors={thanksgivingChoiceErrors}
      setThanksgivingChoiceErrors={setThanksgivingChoiceErrors}
      christmasChoiceErrors={christmasChoiceErrors}
      setChristmasChoiceErrors={setChristmasChoiceErrors}
      expanded={expanded}
      setExpanded={setExpanded}
      isReadOnly={props.isAwardedEmpReadOnlyView}
    />
  );

  const renderPermanentBidPreference = () => (
    <PermanentBidPreference
      permanentBid={permanentBid}
      setPermanentBid={setPermanentBid}
      isDesktopView={props.isDesktopView}
      noPreferencesAlert={noPreferencesAlert}
      setNoPreferencesAlert={setNoPreferencesAlert}
      twuContract={twuContract}
      isReadOnly={props.isAwardedEmpReadOnlyView}
      selectedAwardedBid={props.selectedAwardedBid}
    />
  );

  const CancelButtonEmployeeId = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeDataClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  const handleEmployeeDataClear = () => {
    setEmployeeId("");
    setEmployeeData(null);
    clearBottomForm();
    let errors = validatePermanentBidEmployeeId("");
    if (errors !== "") {
      setEmployeeIdErrors(errors);
    }
  };

  const getBidAdminPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.BID_ADMINISTRATION).path;
  };

  const getBreadCrumbsList = () => {
    let breadcrumbsList = [BREADCRUMBS_TITLE.HOME];

    if (props.isAwardedEmpReadOnlyView) {
      breadcrumbsList.push([
        `${BREADCRUMBS_TITLE.BID_ADMINISTRATION} / ${BREADCRUMBS_TITLE.MANAGE_SHIFT_BID} / ${props.selectedAwardedBid.awardedEid}`,
      ]);
    } else {
      breadcrumbsList.push([BREADCRUMBS_TITLE.MANAGE_PERMANENT_BID]);
    }

    return breadcrumbsList;
  };

  const breadcrumbsLinks = [HOME_PATH, getBidAdminPath()];

  const renderAdminPermanentBidPage = () => {
    return (
      <div className={classes.bidDesktopScroll}>
        <Grid>
          <StarsBreadcrumbs
            isConfirmationRequired={isPermanentBidChanged()}
            breadcrumbsLinks={breadcrumbsLinks}
            breadcrumbsList={getBreadCrumbsList()}
            cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
          />
        </Grid>
        <div className={classes.root}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item container justifyContent="flex-start" md={8} lg={8}>
              <Grid container className={classes.pageTitleLeft}>
                {props.isAwardedEmpReadOnlyView
                  ? `${getBidName(props.bidData)} Permanent Bid`
                  : MANAGE_PERMANENT_BID_TITLE}
              </Grid>
              <br />
              <Grid className={classes.lastUpdated}>
                {lastUpdatedTimestamp !== "" && lastUpdatedByFirstName !== "" && lastUpdatedByLastName !== "" ? (
                  <div>
                    {`${LAST_UPDATED} ${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()} `}
                    {isLastUpdatedByAdmin ? (
                      <Link onClick={() => setViewCommentsDialogOpen(true)} component="button" variant="subtitle2">
                        (View comment)
                      </Link>
                    ) : (
                      ""
                    )}
                  </div>
                ) : (
                  ""
                )}
              </Grid>
            </Grid>
            <Grid item md={4} lg={4}>
              {props.isAwardedEmpReadOnlyView ? (
                <Grid container justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      className={classes.bidButton}
                      variant="outlined"
                      color="primary"
                      onClick={props.handleManagePermBidBackClick}
                    >
                      Back
                    </Button>
                  </Grid>
                </Grid>
              ) : employeeIdSearchTriggered && employeeIdSearchLoaded ? (
                employeeIdErrors === "" && employeeData ? (
                  <Grid container justifyContent="flex-end" spacing={2}>
                    <Grid item>
                      <Button
                        className={classes.bidButton}
                        variant="outlined"
                        color="primary"
                        onClick={handleClickCancelDialogOpen}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        className={classes.bidButton}
                        variant="outlined"
                        color="error"
                        onClick={handleClickDelete}
                        disabled={isPermanentBidEmpty || employeeIdErrors}
                      >
                        Delete
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClickSubmitDialogOpen}
                        disabled={isValidationLoading || !isPermanentBidChanged()}
                      >
                        {isValidationLoading ? <CircularProgress size={25} /> : "Submit"}
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  ""
                )
              ) : (
                ""
              )}
            </Grid>
          </Grid>
          {userSession.isAdmin && (
            <Grid
              container
              sx={{
                pt: 3,
              }}
              spacing={2}
            >
              <Grid item md={4}>
                <div className={classes.bidInputRow}>
                  <div>
                    {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                      <FormControl>
                        <StarsSelectInput
                          label={STATION}
                          menuMessage={STATION}
                          value={stationFilter}
                          allOptions={getMultiStationAdminStationList(userSession)}
                          sortComparator={sortOnCaseSensitiveTexts}
                          onChange={(event) => handleStationFilterChange(event)}
                          errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
                          className={classes.inputSelect}
                          isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
                          disabled={props.isAwardedEmpReadOnlyView}
                        />
                      </FormControl>
                    ) : (
                      <TextField
                        className={classes.inputSelect}
                        label={STATION}
                        variant="outlined"
                        value={stationFilter}
                        disabled
                      />
                    )}
                  </div>
                  <div className={classes.bidInput}>
                    <TextField
                      className={classes.inputText}
                      label={EMPLOYEE_ID}
                      variant="outlined"
                      value={employeeID}
                      onChange={handleEmployeeIdChange}
                      onBlur={handleEmployeeIdBlur}
                      onKeyUp={handleEmployeeIdEnterKeyPressed}
                      InputProps={{
                        endAdornment:
                          employeeID !== "" && !props.isAwardedEmpReadOnlyView ? (
                            <InputAdornment position="start">
                              <CancelButtonEmployeeId />
                            </InputAdornment>
                          ) : (
                            ""
                          ),
                      }}
                      error={employeeIdErrors !== ""}
                      helperText={employeeIdErrors}
                      disabled={props.isAwardedEmpReadOnlyView}
                    />
                  </div>
                  <div>
                    <Button
                      data-testid="myPermanentBid-view-button"
                      className={classes.buttonStyle}
                      variant="contained"
                      color="primary"
                      onClick={() => handleView(employeeID)}
                      disabled={props.isAwardedEmpReadOnlyView}
                    >
                      View
                    </Button>
                  </div>
                </div>
              </Grid>
              <Grid item md={8}>
                {employeeIdSearchTriggered ? (
                  employeeIdSearchLoaded ? (
                    employeeIdErrors === "" && employeeData ? (
                      <Grid container className={classes.employeeDetails}>
                        <Grid item xs={4} className={classes.employeeName}>
                          {employeeData.lastName + ", " + employeeData.firstName}
                        </Grid>
                        <Grid item xs={8} className={classes.employeeData}>
                          <Stack direction="row">
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                HOME STATION
                              </Typography>
                              <p className={classes.employeeDataValue}>{employeeData.homeStation}</p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                WORKGROUP
                              </Typography>
                              <p className={classes.employeeDataValue}>
                                {getWorkgroupGivenJobCode(employeeData.jobCode)}
                              </p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                STATUS
                              </Typography>
                              <p className={classes.employeeDataValue}>{employeeData.ftpt}</p>
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    ) : (
                      ""
                    )
                  ) : (
                    <CircularProgress sx={{ float: "left", marginTop: "7px", marginLeft: "10px" }} />
                  )
                ) : (
                  ""
                )}
              </Grid>
            </Grid>
          )}
          <Grid>
            {employeeIdSearchTriggered && employeeIdSearchLoaded ? (
              employeeIdErrors === "" && employeeData ? (
                <Card className={classes.cardStyle} variant="outlined">
                  <CardHeader
                    titleTypographyProps={{
                      fontSize: "20px",
                      fontWeight: "700px",
                    }}
                    action={
                      <>
                        <Button
                          variant="outlined"
                          startIcon={!exportLoading ? <UploadFileIcon /> : <CircularProgress size={20} />}
                          disabled={isPermanentBidEmpty}
                          onClick={downloadPDF}
                        >
                          {EXPORT_PDF}
                        </Button>
                        <a className={classes.link} ref={linkRef} href={"/"}>
                          Download Report
                        </a>
                      </>
                    }
                    title={
                      contract === IAM_CONTRACT ? CUSTOMER_SERVICE_PERMANENT_BID_MESSAGE : TWU_PERMANENT_BID_MESSAGE
                    }
                  />
                  {contract === IAM_CONTRACT && renderHolidayComponent()}
                  <Divider />
                  <CardContent className={classes.CardContent}>
                    <Alert severity="info" className={classes.bidAlert}>
                      {contract === IAM_CONTRACT
                        ? UPDATE_PERMANENT_BID_FIELDS_ALERT_MESSAGE
                        : UPDATE_PERMANENT_BID_TWU_ALERT_MESSAGE}
                    </Alert>
                    <Divider />
                    <Grid>{renderPermanentBidPreference()}</Grid>
                  </CardContent>
                </Card>
              ) : (
                ""
              )
            ) : (
              ""
            )}
          </Grid>
          {renderCancelAreYouSureDialog()}
          {renderDeleteAreYouSureDialog()}
          {renderSubmitAreYouSureDialog()}
          {isSubmitDialogOpen ? (
            <SuccessDialog
              isSuccessDialogOpen={isSubmitDialogOpen}
              title={isErrorPermanentBidSave ? ERROR_MESSAGE : SUCCESS_TITLE}
              message={PERMANENT_BID_SAVE_AS_ADMIN_MESSAGE}
              hasError={isErrorPermanentBidSave}
              error={PERMANENT_BID_SUBMISSION_ERROR_MESSAGE}
              handleOKClick={() => {
                setIsSubmitDialogOpen(false);
                setIsErrorPermanentBidSave(false);
                setCommentErrorMessage("");
                setComments("");
                fetchPermanentBid();
              }}
            />
          ) : (
            ""
          )}
          {isDeleteSuccessDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isDeleteSuccessDialogOpen}
              title={isErrorPermanentBidDelete ? ERROR_MESSAGE : SUCCESS_TITLE}
              message={PERMANENT_BID_DELETE_AS_ADMIN_MESSAGE}
              hasError={isErrorPermanentBidDelete}
              error={PERMANENT_BID_DELETION_ERROR_MESSAGE}
              handleOKClick={() => {
                setIsDeleteSuccessDialogOpen(false);
                fetchPermanentBid();
                setIsErrorPermanentBidDelete(false);
                setCommentErrorMessage("");
                setComments("");
              }}
            />
          )}
          {viewCommentsDialogOpen && (
            <ViewCommentsDialog
              isDialogOpen={viewCommentsDialogOpen}
              handleClickDialogClose={() => setViewCommentsDialogOpen(false)}
              header={`Last updated by ${lastUpdatedByLastName}, ${lastUpdatedByFirstName} on `}
              publishedTimestamp={getLastUpdatedTimeString()}
              message={lastUpdatedComments}
            />
          )}
        </div>
      </div>
    );
  };
  const renderMobileAdminPermanentBidPage = () => {
    return (
      <div className="page-error" data-testid="admin-mobile-permanent-bid-mobile-page">
        {ADMIN_MOBILE_WARNING_MESSAGE}
      </div>
    );
  };
  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }
  return (
    <div data-testid="admin-permanent-bid-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderAdminPermanentBidPage() : renderMobileAdminPermanentBidPage()}</>
      )}
    </div>
  );
};
export default AdminPermanentBid;
